import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FreeFireGiveawayPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    userId: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('userId', formData.userId);
      formBody.append('email', formData.email);

      const response = await fetch('https://readdy.ai/api/form/d5h9b8jgekujqjrtsll0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', userId: '', email: '' });
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-purple-900 via-purple-800 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/433ce0ef408f3433117df6b763c65ed4.jpg"
            alt="Free Fire Background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-300 mb-6">
            <a href="/" className="hover:text-orange-400 transition-colors cursor-pointer">Home</a>
            <span className="mx-2">&gt;</span>
            <span className="text-white">Free Fire Giveaway Guide</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            Free Fire Diamond Giveaway - Complete Guide
          </h1>
          <div className="inline-flex items-center space-x-2 bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/50 px-4 py-2 rounded-full">
            <i className="ri-information-line text-yellow-400"></i>
            <span className="text-yellow-200 text-sm font-semibold">Fan-Made | Not Affiliated with Garena</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-8">
            {/* Introduction */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                  <i className="ri-information-line text-orange-500 text-xl"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">What is a Free Fire Diamond Giveaway?</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                A Free Fire Diamond Giveaway is an event where content creators, gaming communities, or fan pages distribute free diamonds to Free Fire players. Diamonds are the premium in-game currency used to purchase exclusive items, characters, and skins in Free Fire.
              </p>
              <p className="text-gray-700 leading-relaxed">
                These giveaways are typically organized by fans and gaming enthusiasts to support the Free Fire community. They are <strong>not official Garena events</strong> and are conducted independently by various content creators.
              </p>
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">How Giveaways Work</h2>
              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Announcement',
                    description: 'Giveaway organizers announce the event on social media platforms like Instagram, YouTube, or Facebook with clear rules and requirements.',
                  },
                  {
                    step: 2,
                    title: 'Entry Requirements',
                    description: 'Participants usually need to follow the organizer, like the post, tag friends, or complete simple tasks. Never share your password.',
                  },
                  {
                    step: 3,
                    title: 'Submit Details',
                    description: 'Participants submit their Free Fire User ID and contact information through official entry forms or comment sections.',
                  },
                  {
                    step: 4,
                    title: 'Winner Selection',
                    description: 'Winners are selected randomly or based on specific criteria mentioned in the giveaway rules.',
                  },
                  {
                    step: 5,
                    title: 'Reward Distribution',
                    description: 'Winners receive diamonds through official Free Fire top-up methods or gift codes shared by the organizer.',
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-full font-bold text-lg flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.description}</p>
                    </div>
                    {index < 4 && (
                      <div className="hidden sm:block">
                        <i className="ri-arrow-down-line text-gray-300 text-2xl"></i>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Safe Participation Guide */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 shadow-md border-2 border-yellow-200">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-yellow-500 rounded-lg">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">How to Participate Safely</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-green-700 mb-4 flex items-center space-x-2">
                    <i className="ri-check-line"></i>
                    <span>DO's - Safe Practices</span>
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'Only share your Free Fire User ID (visible in-game)',
                      'Verify the organizer\'s credibility and social media presence',
                      'Read all giveaway rules and terms carefully',
                      'Use a secondary email for giveaway entries',
                      'Check for official announcements on trusted platforms',
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className="ri-checkbox-circle-fill text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-red-700 mb-4 flex items-center space-x-2">
                    <i className="ri-close-line"></i>
                    <span>DON'Ts - Avoid These</span>
                  </h3>
                  <ul className="space-y-3">
                    {[
                      'NEVER share your Free Fire account password',
                      'Don\'t share OTP codes or verification codes',
                      'Avoid giveaways asking for payment or fees',
                      'Don\'t click on suspicious links or download unknown apps',
                      'Never share personal banking or payment information',
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700 font-semibold">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded-lg">
                  <p className="text-red-800 font-bold text-lg">
                    <i className="ri-alert-line mr-2"></i>
                    Never share your password with anyone!
                  </p>
                </div>
              </div>
            </div>

            {/* Rewards Information */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 shadow-md">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-purple-500 rounded-lg">
                  <i className="ri-vip-diamond-fill text-white text-xl"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Rewards Information</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                Giveaway rewards typically include Free Fire diamonds in various quantities. The exact amount depends on the organizer and the giveaway budget.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-purple-200">
                      <th className="px-6 py-3 text-left text-sm font-bold text-gray-900">Reward Tier</th>
                      <th className="px-6 py-3 text-left text-sm font-bold text-gray-900">Diamond Amount</th>
                      <th className="px-6 py-3 text-left text-sm font-bold text-gray-900">Typical Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-gray-700">Basic</td>
                      <td className="px-6 py-4 text-gray-700">100-500 Diamonds</td>
                      <td className="px-6 py-4 text-gray-700">₹80-₹400</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-gray-700">Standard</td>
                      <td className="px-6 py-4 text-gray-700">500-1000 Diamonds</td>
                      <td className="px-6 py-4 text-gray-700">₹400-₹800</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-gray-700">Premium</td>
                      <td className="px-6 py-4 text-gray-700">1000-2000 Diamonds</td>
                      <td className="px-6 py-4 text-gray-700">₹800-₹1600</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-gray-700">Grand Prize</td>
                      <td className="px-6 py-4 text-gray-700">2000+ Diamonds</td>
                      <td className="px-6 py-4 text-gray-700">₹1600+</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Giveaway Duration */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Giveaway Duration &amp; Announcement</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                    <i className="ri-calendar-line text-blue-500 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Duration</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Most giveaways run for 3-7 days, giving participants enough time to enter. The exact duration is mentioned in the giveaway announcement post.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-notification-line text-green-500 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Winner Announcement</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Winners are typically announced on the organizer's social media pages (Instagram, YouTube, Facebook) within 1-3 days after the giveaway ends. Winners are also contacted via email or direct message.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Giveaway Entry Form */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-4 border-orange-200">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mb-4">
                  <i className="ri-megaphone-fill text-white text-3xl"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Giveaway Entry / Comment Section</h2>
                <p className="text-gray-600">Submit Your Details to Participate</p>
              </div>

              {/* Important Notes */}
              <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-8">
                <h3 className="text-lg font-bold text-orange-900 mb-4 flex items-center space-x-2">
                  <i className="ri-alert-line"></i>
                  <span>Important Notes</span>
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <i className="ri-close-circle-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Do not share your password</strong></span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-mail-line text-blue-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Details are collected only for giveaway communication</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-information-line text-yellow-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Submission does not guarantee winning</span>
                  </li>
                </ul>
              </div>

              {/* Form */}
              <form id="freefire-giveaway-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-gray-900"
                  />
                </div>

                <div>
                  <label htmlFor="userId" className="block text-sm font-bold text-gray-900 mb-2">
                    Free Fire User ID *
                  </label>
                  <input
                    type="text"
                    id="userId"
                    name="userId"
                    value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    required
                    placeholder="Enter your Free Fire User ID"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    <i className="ri-information-line mr-1"></i>
                    Find your User ID in Free Fire game profile
                  </p>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-gray-900"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Entry'}
                </button>

                {submitSuccess && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-center space-x-3">
                    <i className="ri-checkbox-circle-fill text-green-500 text-2xl"></i>
                    <span className="text-green-800 font-semibold">Entry Submitted Successfully!</span>
                  </div>
                )}
              </form>
            </div>

            {/* Disclaimer */}
            <div className="bg-gray-100 rounded-2xl p-8 shadow-md">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Disclaimer</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>InfoDrop.in</strong> is a fan-made information platform and is <strong>not affiliated with, endorsed by, or connected to Garena, Free Fire, or any official game developers</strong>.
                </p>
                <p>
                  All giveaway information provided on this website is for educational purposes only. We do not organize or conduct giveaways ourselves. We simply provide information about how giveaways work and safety guidelines.
                </p>
                <p>
                  Free Fire and all related trademarks are the property of Garena International. We respect all intellectual property rights.
                </p>
                <p className="font-bold text-red-600">
                  Always verify the authenticity of any giveaway before participating. Never share your account password with anyone.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Quick Links */}
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Links</h3>
                <ul className="space-y-3">
                  <li>
                    <a href="#" className="text-orange-500 hover:text-orange-600 font-semibold flex items-center space-x-2 cursor-pointer">
                      <i className="ri-arrow-right-s-line"></i>
                      <span>What is Giveaway?</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-orange-500 hover:text-orange-600 font-semibold flex items-center space-x-2 cursor-pointer">
                      <i className="ri-arrow-right-s-line"></i>
                      <span>How It Works</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-orange-500 hover:text-orange-600 font-semibold flex items-center space-x-2 cursor-pointer">
                      <i className="ri-arrow-right-s-line"></i>
                      <span>Safety Guide</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="text-orange-500 hover:text-orange-600 font-semibold flex items-center space-x-2 cursor-pointer">
                      <i className="ri-arrow-right-s-line"></i>
                      <span>Submit Entry</span>
                    </a>
                  </li>
                </ul>
              </div>

              {/* Safety Tips */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 shadow-md border-2 border-green-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <i className="ri-shield-check-line text-green-500"></i>
                  <span>Safety Tips</span>
                </h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Only share User ID</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Verify organizer credibility</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Never pay for giveaways</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700">Avoid suspicious links</span>
                  </li>
                </ul>
              </div>

              {/* Related Links */}
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Related</h3>
                <ul className="space-y-3">
                  <li>
                    <a href="/bgmi-giveaway" className="text-gray-700 hover:text-orange-500 text-sm flex items-center space-x-2 cursor-pointer">
                      <i className="ri-gamepad-line"></i>
                      <span>BGMI UC Giveaway</span>
                    </a>
                  </li>
                  <li>
                    <a href="/tech-blog" className="text-gray-700 hover:text-orange-500 text-sm flex items-center space-x-2 cursor-pointer">
                      <i className="ri-article-line"></i>
                      <span>Tech Guides</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FreeFireGiveawayPage;
